import { useEffect, useRef, useState } from 'react';
import { api, type InboxRead, type Presence } from '../lib/api.ts';
import { T, tx } from '../lib/i18n.ts';
import { useApp } from '../state/app.tsx';
import { TEST_ALERT_EVENT, planAlert, type AlertPlan } from '../lib/inpage-alerts.ts';
import { playTone, speakSmart, toneFor } from '../lib/voice.ts';

/**
 * 网页开着时的页面内提醒（2026-10-29 接上）。挂在外框里，每一页都在。
 *
 * 每 30 秒问一次收件箱，**只弹打开网页之后才来的**（之前的那些已经推到手机上了，不重复打扰）。
 *
 * 以前熔断期间还会把这个网站别的页盖住 —— 老板 2026-10-30 指出这没意义：用户不在我们网站上交易，
 * 盖住我们的网站只会挡住他看数据、改设置。删掉了。熔断改由浏览器扩展提醒（图标角标倒计时 + 一条系统通知），
 * 总览顶上的倒计时横幅照旧，手机推送照旧。
 */

const C = {
  ok: T('知道了', '知道了', 'Got it'),
  open: T('打开', '打開', 'Open'),
} as const;

interface Pop { key: string; title: string; body: string; url: string | null; plan: AlertPlan }

export function InPageAlerts(): React.ReactElement | null {
  const app = useApp();
  const lang = app.lang;
  const [pops, setPops] = useState<Pop[]>([]);
  const seen = useRef(new Set<string>());
  const since = useRef(Date.now());
  const settings = useRef(app.settings);
  settings.current = app.settings;

  /* 提醒：只看打开网页之后才来的 */
  useEffect(() => {
    if (app.guest) return;
    let alive = true;
    const pull = async (): Promise<void> => {
      if (document.visibilityState !== 'visible') return;
      const r = await api.get<InboxRead>('/inbox?limit=10');
      if (!alive || !r.ok) return;
      /* 2026-10-30 起每一种提醒都弹（以前只弹耐心、久坐、心法、事项四种） */
      const fresh = r.data.items.filter(i => i.atMs >= since.current && !seen.current.has(i.key));
      for (const i of fresh) seen.current.add(i.key);
      const next: Pop[] = [];
      /* 浏览器扩展在线时它已经在电脑上弹了系统通知、响了、念了：网页这边只弹，不再出声，免得响两遍 */
      let extOn = false;
      if (fresh.length > 0) {
        const pr = await api.get<Presence>('/presence');
        extOn = pr.ok && pr.data.extensionAlive === true;
      }
      for (const i of fresh.reverse()) {
        const plan = planAlert(i.kind, i.title, i.body, settings.current, lang, Math.random, i.urgency);
        if (!plan) continue;
        /* 响哪种照「声音与语音」的弹窗提示音（ge_tone）和全局静音时段；这张卡上的「提示音」开关关了就不响 */
        const tone = plan.sound && !extOn ? toneFor(settings.current, i.urgency === 'critical') : null;
        if (tone) playTone(tone);
        /* 念：优先 Edge 神经语音，不通退回浏览器自带的（lib/voice.ts 的 speakSmart） */
        if (plan.speak && !extOn) void speakSmart(plan.speak, settings.current, lang);
        if (plan.style !== 'none') next.push({ key: i.key, title: i.title, body: i.body, url: i.url, plan });
      }
      if (next.length > 0) setPops(p => [...p, ...next].slice(-3));
    };
    const t = setInterval(() => void pull(), 30_000);
    return () => { alive = false; clearInterval(t); };
  }, [app.guest, lang]);

  /* 「测试提醒效果」按下去：马上照那一种的样子弹、响、念（扩展在线时它在电脑上已经响了，网页只弹） */
  useEffect(() => {
    const on = (e: Event): void => {
      const d = (e as CustomEvent<{ kind: string; title: string; body: string; ext: boolean }>).detail;
      if (!d) return;
      const plan = planAlert(d.kind, d.title, d.body, settings.current, lang, Math.random, 'active');
      if (!plan) return;
      const tone = plan.sound && !d.ext ? toneFor(settings.current, false) : null;
      if (tone) playTone(tone);
      if (plan.speak && !d.ext) void speakSmart(plan.speak, settings.current, lang);
      if (plan.style !== 'none') setPops(p => [...p, { key: 'test:' + Date.now(), title: d.title, body: d.body, url: null, plan }].slice(-3));
    };
    window.addEventListener(TEST_ALERT_EVENT, on);
    return () => window.removeEventListener(TEST_ALERT_EVENT, on);
  }, [lang]);

  /* 自动消失的那几个（极度耐心按 pa_duration） */
  useEffect(() => {
    const timers = pops.filter(p => p.plan.ms > 0).map(p => setTimeout(() => setPops(x => x.filter(y => y.key !== p.key)), p.plan.ms));
    return () => { for (const t of timers) clearTimeout(t); };
  }, [pops]);

  const close = (key: string): void => setPops(p => p.filter(x => x.key !== key));

  return (
    <>
      {pops.filter(p => p.plan.style === 'toast').length > 0 ? (
        <div className="inpage-toasts" role="status">
          {pops.filter(p => p.plan.style === 'toast').map(p => (
            <div className="inpage-toast" key={p.key}>
              <b>{p.title}</b>
              {p.body ? <div className="hint" style={{ whiteSpace: 'pre-wrap' }}>{p.body}</div> : null}
              <button type="button" className="btn sm" onClick={() => close(p.key)}>{tx(C.ok, lang)}</button>
            </div>
          ))}
        </div>
      ) : null}
      {pops.filter(p => p.plan.style === 'center' || p.plan.style === 'full').slice(-1).map(p => (
        <div key={p.key} className={p.plan.style === 'full' ? 'modal-back inpage-full' : 'modal-back'} role="alertdialog" aria-modal="true" aria-labelledby={'ia-' + p.key}
          onClick={e => { if (e.target === e.currentTarget && p.plan.style !== 'full') close(p.key); }}>
          <div className="modal narrow">
            <div className="modal-h"><b id={'ia-' + p.key}>{p.title}</b></div>
            <div className="modal-b" style={{ display: 'grid', gap: 10, lineHeight: 1.7 }}>
              {p.body ? <div style={{ whiteSpace: 'pre-wrap' }}>{p.body}</div> : null}
              {p.plan.extra ? <div className="hint">{p.plan.extra}</div> : null}
            </div>
            <div className="modal-f">
              {p.url ? <a className="btn" href={p.url} target="_blank" rel="noopener noreferrer">{tx(C.open, lang)}</a> : <span />}
              <button type="button" className="btn primary" onClick={() => close(p.key)}>{tx(C.ok, lang)}</button>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}
